/**
 * Алгебра посимвольных прогонов (styleRuns) для пословной окраски и правок текста.
 *
 * Прогоны — полуинтервалы [from, to) в координатах СТРОКИ МОДЕЛИ (params.text / drawable.text.text),
 * непокрытые участки наследуют блочные свойства. Все функции чистые и не знают про Angular/модель —
 * их делят design-editor (params.styleRuns) и album-editor (templateMeta.styleRuns).
 */

export interface StyleRunRange {
  from: number;
  to: number;
  color?: string;
}

interface Segment<T extends StyleRunRange> {
  from: number;
  to: number;
  /** Прогон-источник свойств сегмента; null — сегмент не был покрыт прогоном (блочный стиль). */
  base: T | null;
}

/** Нарезает [0, textLength) на сегменты по границам прогонов; протухшие границы зажимаются в длину текста. */
function segmentsOf<T extends StyleRunRange>(runs: readonly T[], textLength: number): Segment<T>[] {
  const bounds = new Set<number>([0, textLength]);
  for (const r of runs) {
    bounds.add(Math.max(0, Math.min(textLength, r.from)));
    bounds.add(Math.max(0, Math.min(textLength, r.to)));
  }

  const sorted = [...bounds].sort((a, b) => a - b);
  const out: Segment<T>[] = [];
  for (let i = 0; i + 1 < sorted.length; i++) {
    const from = sorted[i];
    const to = sorted[i + 1];
    if (to <= from) continue;
    const base = runs.find(r => r.from <= from && r.to >= to) ?? null;
    out.push({ from, to, base });
  }

  return out;
}

function runProps<T extends StyleRunRange>(run: T): Omit<T, 'from' | 'to'> {
  const { from: _f, to: _t, ...props } = run;
  return props;
}

function sameProps(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keysA = Object.keys(a).filter(k => a[k] !== undefined);
  const keysB = Object.keys(b).filter(k => b[k] !== undefined);
  return keysA.length === keysB.length && keysA.every(k => a[k] === b[k]);
}

/** Собирает сегменты обратно в прогоны: пустые (без свойств) выпадают, соседние одинаковые сливаются. */
function mergeSegments<T extends StyleRunRange>(
  segments: readonly (Segment<T> & { override?: { color?: string } })[],
): StyleRunRange[] {
  const out: StyleRunRange[] = [];
  for (const seg of segments) {
    const props: Record<string, unknown> = seg.base ? { ...runProps(seg.base) } : {};
    if (seg.override && 'color' in seg.override) {
      if (seg.override.color === undefined) delete props['color'];
      else props['color'] = seg.override.color;
    }

    for (const k of Object.keys(props)) if (props[k] === undefined) delete props[k];
    if (Object.keys(props).length === 0) continue;
    const prev = out[out.length - 1];
    if (prev && prev.to === seg.from && sameProps(runProps(prev as StyleRunRange & Record<string, unknown>), props)) {
      prev.to = seg.to;
    } else {
      out.push({ from: seg.from, to: seg.to, ...props });
    }
  }

  return out;
}

/**
 * Красит [from, to) в `color`. Выбор ровно блочного цвета = «распокрасить» — цвет с диапазона снимается,
 * и он наследует блочный. Возвращает нормализованный список прогонов (может быть пуст — ключ можно убрать);
 * null — красить нечего (пустой/вырожденный диапазон).
 */
export function applyColorToStyleRuns<T extends StyleRunRange>(
  runs: readonly T[] | undefined,
  textLength: number,
  from: number,
  to: number,
  color: string,
  blockColor: string,
): StyleRunRange[] | null {
  const a = Math.max(0, Math.min(textLength, Math.min(from, to)));
  const b = Math.max(0, Math.min(textLength, Math.max(from, to)));
  if (b <= a) return null;

  const paintsBlock = color.toLowerCase() === blockColor.toLowerCase();
  const segments = segmentsOf(runs ?? [], textLength)
    .flatMap(seg => {
      const cut: Segment<T>[] = [];
      const points = [seg.from, Math.max(seg.from, Math.min(seg.to, a)), Math.max(seg.from, Math.min(seg.to, b)), seg.to];
      for (let i = 0; i + 1 < points.length; i++) {
        if (points[i + 1] > points[i]) cut.push({ from: points[i], to: points[i + 1], base: seg.base });
      }

      return cut;
    })
    .map(seg => {
      const inside = seg.from >= a && seg.to <= b;
      if (!inside) return seg;
      return { ...seg, override: { color: paintsBlock ? undefined : color } };
    });

  return mergeSegments(segments);
}

/** Уникальные фактические цвета диапазона [from, to): цвет прогона либо блочный на непокрытых участках. */
export function styleRunColorsInRange<T extends StyleRunRange>(
  runs: readonly T[] | undefined,
  textLength: number,
  from: number,
  to: number,
  blockColor: string,
): string[] {
  const a = Math.max(0, Math.min(textLength, Math.min(from, to)));
  const b = Math.max(0, Math.min(textLength, Math.max(from, to)));
  if (b <= a) return [];
  const colors = new Set<string>();
  for (const seg of segmentsOf(runs ?? [], textLength)) {
    if (seg.to <= a || seg.from >= b) continue;
    colors.add((seg.base?.color ?? blockColor).toLowerCase());
  }

  return [...colors];
}

/**
 * Пересчитывает границы прогонов после правки текста, найденной диффом (общий префикс/суффикс).
 * Вставка на границе наследует стиль СЛЕВА; полное замещение (нет общих префикса и суффикса) сбрасывает
 * прогоны целиком — новый текст живёт блочным стилем. undefined на входе → undefined (прогонов не было).
 */
export function remapStyleRunsForTextChange<T extends StyleRunRange>(
  runs: readonly T[] | undefined,
  oldText: string,
  newText: string,
): StyleRunRange[] | undefined {
  if (!runs || runs.length === 0) return runs ? [] : undefined;
  if (oldText === newText) return [...runs];

  let prefix = 0;
  const maxPrefix = Math.min(oldText.length, newText.length);
  while (prefix < maxPrefix && oldText[prefix] === newText[prefix]) prefix++;
  let suffix = 0;
  const maxSuffix = Math.min(oldText.length, newText.length) - prefix;
  while (suffix < maxSuffix && oldText[oldText.length - 1 - suffix] === newText[newText.length - 1 - suffix]) suffix++;

  // Полное замещение — переносить нечего: индексы старого текста не значат ничего в новом.
  if (prefix === 0 && suffix === 0) return [];

  const oldEnd = oldText.length - suffix;
  const delta = newText.length - oldText.length;
  const insLen = newText.length - suffix - prefix;

  // to == prefix при чистой вставке (oldEnd == prefix) уходит в ветку p >= oldEnd и растягивается —
  // набор сразу ПОСЛЕ окрашенного слова продолжает его цвет (наследование буквы слева, как в Word/PS).
  const mapFrom = (p: number): number => (p < prefix ? p : p >= oldEnd ? p + delta : prefix);
  const mapTo = (p: number): number => (p < prefix ? p : p >= oldEnd ? p + delta : prefix + insLen);

  const mapped: T[] = [];
  for (const r of runs) {
    const from = Math.max(0, Math.min(newText.length, mapFrom(r.from)));
    const to = Math.max(0, Math.min(newText.length, mapTo(r.to)));
    if (to > from) mapped.push({ ...r, from, to });
  }

  // Смаппленные (и легаси-повреждённые входные) прогоны могут пересекаться — пересобираем ось в
  // непересекающиеся сегменты; на пересечении побеждает более ранний прогон списка.
  return mergeSegments(segmentsOf(mapped, newText.length));
}

/** A paragraph-run entry: `[from, to)` in UTF-16 code units of the text — one paragraph without its `\n`. */
export interface ParagraphRunRange {
  from: number;
  to: number;
}

/**
 * The entry holding the paragraph that starts at `start` (an empty paragraph has an empty range at its start) — how
 * both layouts look a paragraph's attributes up (mirror of Rust `glyph_layout/paragraphs.rs` `holds`).
 */
export function paragraphRunHolds(run: ParagraphRunRange, start: number): boolean {
  return run.from <= start && (start < run.to || (run.from === run.to && run.from === start));
}

/** Where `newText` differs from `oldText`: `[prefix, oldEnd)` became `[prefix, newEnd)`. */
function textEditOf(oldText: string, newText: string): { prefix: number; oldEnd: number; newEnd: number } {
  let prefix = 0;
  const maxPrefix = Math.min(oldText.length, newText.length);
  while (prefix < maxPrefix && oldText[prefix] === newText[prefix]) prefix++;
  let suffix = 0;
  const maxSuffix = maxPrefix - prefix;
  while (suffix < maxSuffix && oldText[oldText.length - 1 - suffix] === newText[newText.length - 1 - suffix]) suffix++;
  let oldEnd = oldText.length - suffix;
  let newEnd = newText.length - suffix;
  // Enter at a paragraph's end and at the next one's start give the same text: take the leftmost place, so a new
  // paragraph opens after the current one and deleting an empty line leaves both neighbours as they were.
  const inserted = oldEnd === prefix;
  const changed = inserted ? newText : newEnd === prefix ? oldText : undefined;
  while (changed !== undefined && prefix > 0 && changed[prefix - 1] === changed[(inserted ? newEnd : oldEnd) - 1]) {
    prefix--;
    oldEnd--;
    newEnd--;
  }

  return { prefix, oldEnd, newEnd };
}

const paragraphBreaksIn = (text: string, from: number, to: number): number => {
  let n = 0;
  for (let i = from; i < to; i++) if (text[i] === '\n') n++;
  return n;
};

/**
 * Абзацные прогоны (`paragraphRuns`, textLayout 7) после правки текста: каждая запись — ровно один абзац НОВОГО
 * текста. Абзац держит атрибуты, которые раскладка показывала до правки: набор в его начало их не снимает; Enter
 * отдаёт атрибуты абзаца обеим половинам (в конце абзаца — новому пустому); слияние абзацев оставляет атрибуты
 * первого; пустые абзацы сохраняют свои записи. Текст, замещённый целиком, — та же правка с начала: его абзацы
 * берут атрибуты первого прежнего абзаца.
 */
export function remapParagraphRunsForTextChange<T extends ParagraphRunRange>(
  runs: readonly T[] | undefined,
  oldText: string,
  newText: string,
): T[] | undefined {
  if (!runs || runs.length === 0) return runs ? [] : undefined;
  if (oldText === newText) return [...runs];
  const edit = textEditOf(oldText, newText);
  const shown: (T | undefined)[] = [];
  let start = 0;
  for (const para of oldText.split('\n')) {
    shown.push(runs.find(r => paragraphRunHolds(r, start)));
    start += para.length + 1;
  }

  // Paragraphs up to the edited one keep their index; those it opened take its attributes; the rest shift.
  const edited = paragraphBreaksIn(oldText, 0, edit.prefix);
  const opened = paragraphBreaksIn(newText, edit.prefix, edit.newEnd);
  const closed = paragraphBreaksIn(oldText, edit.prefix, edit.oldEnd);
  const out: T[] = [];
  start = 0;
  newText.split('\n').forEach((para, i) => {
    const source = i <= edited ? i : i <= edited + opened ? edited : i - opened + closed;
    const run = shown[source];
    if (run) out.push({ ...run, from: start, to: start + para.length });
    start += para.length + 1;
  });

  return out;
}

/**
 * Оба глиф-бекенда красят глиф цветом его style-run'а, когда тот отличается от блочного, — поэтому
 * блочная смена цвета обязана снимать цвет со всех runs, иначе новый цвет виден только в пипетке.
 * Возвращает undefined, когда снимать нечего (нет runs или ни одного цвета) — вызывающий не трогает модель.
 */
export function stripStyleRunColors<T extends { color?: string }>(runs: readonly T[] | undefined): T[] | undefined {
  if (!runs?.some(r => r.color !== undefined)) return undefined;
  return runs.map(r => {
    const { color: _lifted, ...rest } = r;
    return rest as T;
  });
}

/** Which run decorations a block-level patch sets: those runs must give up theirs. */
export interface StyleRunDecorationPatch {
  underline?: boolean;
  strikethrough?: boolean;
}

/**
 * Оба рендера рисуют подчёркивание и зачёркивание по style-run'ам: флаг run'а перебивает блочный. Поэтому
 * блочное включение или выключение обязано снимать этот флаг со всех runs, иначе кнопка панели не выключит
 * подчёркнутый фрагмент. Возвращает undefined, когда снимать нечего — вызывающий не трогает модель.
 */
export function stripStyleRunDecorations<T extends StyleRunRange & { underline?: boolean; strikethrough?: boolean }>(
  runs: readonly T[] | undefined,
  patch: StyleRunDecorationPatch,
): T[] | undefined {
  const keys = (['underline', 'strikethrough'] as const).filter(k => patch[k]);
  if (!keys.length || !runs?.some(r => keys.some(k => r[k] !== undefined))) return undefined;
  return runs.map(r =>
    keys.reduce((run, key) => {
      const { [key]: _lifted, ...rest } = run;
      return rest as T;
    }, r),
  );
}

/**
 * Оба рендера берут регистр глифа из style-run'а раньше блочного (`run.fontCaps ?? box.fontCaps`), поэтому блочная
 * смена регистра обязана снимать его со всех runs — включая «как есть» (0): иначе слово со своим регистром так и
 * останется заглавным. Возвращает undefined, когда снимать нечего — вызывающий не трогает модель.
 */
export function stripStyleRunCaps<T extends { fontCaps?: number }>(runs: readonly T[] | undefined): T[] | undefined {
  if (!runs?.some(r => r.fontCaps !== undefined)) return undefined;
  return runs.map(r => {
    const { fontCaps: _caps, ...rest } = r;
    return rest as T;
  });
}

/** The block fields a text patch sets; a field present (not null) takes its run overrides away. */
export interface StyleRunOverridePatch {
  color?: unknown;
  underline?: unknown;
  strikethrough?: unknown;
  caps?: unknown;
}

/**
 * One step for every text patch: a block colour strips the run colours, a block underline / strikethrough the run
 * flags, a block case the run cases ({@link stripStyleRunColors}, {@link stripStyleRunDecorations},
 * {@link stripStyleRunCaps}). Undefined when no run changes.
 */
export function stripStyleRunOverrides<
  T extends StyleRunRange & { color?: string; underline?: boolean; strikethrough?: boolean; fontCaps?: number },
>(runs: readonly T[] | undefined, patch: StyleRunOverridePatch): T[] | undefined {
  const colorStripped = patch.color != null ? stripStyleRunColors(runs) : undefined;
  const decorStripped =
    stripStyleRunDecorations(colorStripped ?? runs, {
      underline: patch.underline != null,
      strikethrough: patch.strikethrough != null,
    }) ?? colorStripped;
  return (patch.caps != null ? stripStyleRunCaps(decorStripped ?? runs) : undefined) ?? decorStripped;
}

/**
 * Вес и курсив run'а движок берёт раньше блочных, поэтому блочная смена веса, курсива или гарнитуры обязана снимать
 * их со всех runs — как перекраска снимает цвет: иначе нажатый на блоке Bold не дойдёт до run'а со своим весом.
 * Возвращает undefined, когда снимать нечего (нет runs или ни одного веса/курсива) — вызывающий не трогает модель.
 */
export function stripStyleRunFaces<T extends { fontWeight?: string; italic?: boolean }>(
  runs: readonly T[] | undefined,
): T[] | undefined {
  if (!runs?.some(r => r.fontWeight !== undefined || r.italic !== undefined)) return undefined;
  return runs.map(r => {
    const { fontWeight: _weight, italic: _italic, ...rest } = r;
    return rest as T;
  });
}

/** Поле патча задано (не null) — run'ы отдают своё; `faces` — вес и курсив run'а. */
export interface TextRunStrips {
  color: boolean;
  faces: boolean;
  caps: boolean;
  decorations: { underline: boolean; strikethrough: boolean };
}

/** `fontCaps` — регистр в `Partial<TextBoxParams>`; нынешние вызывающие шлют патч панели с `caps`. */
export type TextRunStripKey =
  | 'color'
  | 'fontName'
  | 'fontWeight'
  | 'bold'
  | 'italic'
  | 'caps'
  | 'fontCaps'
  | 'underline'
  | 'strikethrough';

// Слабый тип пропустит и целый `TextBoxParams` — подавай только ПАТЧ: заданное поле снимает run'ы.
export type TextRunStripPatch = { readonly [K in TextRunStripKey]?: unknown };

// Модель и мосты inline-сессии обязаны снимать одно и то же, иначе коммит сессии вернёт снятое; `fontCaps: 0` тоже снимает.
export function textRunStripsOf(p: TextRunStripPatch): TextRunStrips {
  return {
    color: p.color != null,
    faces: p.fontName != null || p.fontWeight != null || p.bold != null || p.italic != null,
    caps: p.caps != null || p.fontCaps != null,
    decorations: { underline: p.underline != null, strikethrough: p.strikethrough != null },
  };
}

/** Undefined, когда ни один run не меняется — вызывающий не трогает модель. */
export function stripStyleRunsForPatch<
  T extends StyleRunRange & {
    color?: string;
    underline?: boolean;
    strikethrough?: boolean;
    fontCaps?: number;
    fontWeight?: string;
    italic?: boolean;
  },
>(runs: readonly T[] | undefined, p: TextRunStripPatch): T[] | undefined {
  const strips = textRunStripsOf(p);
  const overridden = stripStyleRunOverrides(runs, {
    color: strips.color || undefined,
    caps: strips.caps || undefined,
    underline: strips.decorations.underline || undefined,
    strikethrough: strips.decorations.strikethrough || undefined,
  });
  return (strips.faces && stripStyleRunFaces(overridden ?? runs)) || overridden;
}
